import type * as Y from 'yjs';
import { cardsMap, parseCard } from '../../shared/doc.js';
import type { ProjectConn } from '../doc/useProject.js';
import { useYVersion } from '../doc/useY.js';
import { fetchPreview } from './create.js';
import { FindingCard } from './FindingCard.js';
import { ImageCard } from './ImageCard.js';
import { LinkCard } from './LinkCard.js';
import { NoteCard } from './NoteCard.js';
import { PaletteCard } from './PaletteCard.js';
import { PlaceCard } from './PlaceCard.js';
import { TodoCard } from './TodoCard.js';

export interface BodyProps {
  conn: ProjectConn;
  projectId: string;
  cardId: string;
  editing: boolean;
  onDoneEditing: () => void;
  /** Closes the panel this card is shown in, if any (a place's "Show on map" link follows this). */
  onClose?: () => void;
}

/** Renders any card by kind. Unknown or invalid data shows a placeholder instead of breaking the board. */
export function CardBody({ conn, projectId, cardId, editing, onDoneEditing, onClose }: BodyProps) {
  const cardY = cardsMap(conn.doc).get(cardId) as Y.Map<any> | undefined;
  useYVersion(cardY);
  if (!cardY) return <div className="card-invalid">This card was deleted.</div>;
  const card = parseCard(cardY.toJSON());
  if (!card) return <div className="card-invalid">Can't display this card{typeof cardY.get('kind') === 'string' ? ` (${cardY.get('kind')})` : ''}.</div>;
  switch (card.kind) {
    case 'note':
      return <NoteCard conn={conn} card={cardY} editing={editing} onDone={onDoneEditing} />;
    case 'image':
      return <ImageCard card={card} />;
    case 'link':
      return <LinkCard card={card} onRetry={() => void fetchPreview(conn, projectId, card.id, card.url)} />;
    case 'palette':
      return <PaletteCard conn={conn} card={card} editing={editing} />;
    case 'todo':
      return <TodoCard conn={conn} card={card} editing={editing} />;
    case 'finding':
      return <FindingCard conn={conn} card={card} editing={editing} />;
    case 'place':
      return <PlaceCard conn={conn} projectId={projectId} card={card} editing={editing} onClose={onClose} />;
  }
}
